import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowLeft } from "lucide-react";
import { Button } from "../ui/button";
import "./expanding-workspace.css";

/** Keeps the feed state in its parent while its surface grows into a modal workspace. */
export function ExpandingWorkspace({
  expanded,
  dismissible = true,
  motion = "expand",
  onClose,
  onAfterClose,
  children,
  title = "Autopilot",
  description = "Browse ideas and configure content sources. Escape returns to Generate.",
  backLabel = "Back to Generate",
}: {
  expanded: boolean;
  dismissible?: boolean;
  motion?: "expand" | "fade";
  onClose: () => void;
  onAfterClose?: () => void;
  children: ReactNode;
  title?: string;
  description?: string;
  /** Names the page the workspace returns to. */
  backLabel?: string;
}) {
  const anchor = useRef<HTMLDivElement>(null);
  const origin = useRef({ x: 0, y: 0, width: 1, height: 1 });
  const returnFocus = useRef<HTMLElement | null>(null);
  const returnFocusLabel = useRef("");
  const openedOnMount = useRef(expanded);
  const [present, setPresent] = useState(false);
  const [open, setOpen] = useState(false);
  const [closeAutoFocusPending, setCloseAutoFocusPending] = useState(false);

  const rememberOrigin = () => {
    if (present || !anchor.current) return;
    origin.current = anchor.current.getBoundingClientRect();
    returnFocus.current = document.activeElement as HTMLElement;
    returnFocusLabel.current = returnFocus.current?.textContent || "";
  };

  useLayoutEffect(() => {
    if (expanded) {
      rememberOrigin();
      setPresent(true);
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [expanded]);

  const finishClose = useCallback(() => {
    openedOnMount.current = false;
    setPresent(false);
    onClose();
  }, [onClose]);

  // Radix's close event can run before React commits removal of the tall
  // placeholder. Notify the caller only after the compact children are back
  // in the DOM, otherwise smooth scrolling targets the expanded layout.
  useLayoutEffect(() => {
    if (!closeAutoFocusPending || present || expanded) return;
    const frame = requestAnimationFrame(() => {
      if (returnFocus.current?.isConnected) {
        returnFocus.current.focus({ preventScroll: true });
      } else {
        const buttons = Array.from(
          anchor.current?.querySelectorAll<HTMLButtonElement>("button") || [],
        );
        (buttons.find(button => button.textContent === returnFocusLabel.current)
          || buttons[0])?.focus({ preventScroll: true });
      }
      setCloseAutoFocusPending(false);
      onAfterClose?.();
    });
    return () => cancelAnimationFrame(frame);
  }, [closeAutoFocusPending, present, expanded, onAfterClose]);

  // Also complete the exit if animations are disabled or interrupted by the browser.
  useEffect(() => {
    if (!present || open) return;
    const timer = window.setTimeout(finishClose, motion === "fade" ? 140 : 240);
    return () => window.clearTimeout(timer);
  }, [present, open, finishClose, motion]);

  const animationStyle = {
    "--workspace-x": `${origin.current.x}px`,
    "--workspace-y": `${origin.current.y}px`,
    "--workspace-scale-x":
      origin.current.width / Math.max(window.innerWidth, 1),
    "--workspace-scale-y":
      origin.current.height / Math.max(window.innerHeight, 1),
  } as CSSProperties;
  // Direct arrivals (such as onboarding) have no visible card to expand from.
  const entryMotion = openedOnMount.current && open ? "none" : motion;

  return (
    <>
      <div ref={anchor} onClickCapture={rememberOrigin}>
        {present ? <div style={{ height: origin.current.height }} /> : children}
      </div>
      <Dialog.Root open={open} onOpenChange={(next) => { if (next || dismissible) setOpen(next); }}>
        <Dialog.Portal>
          <Dialog.Overlay data-motion={entryMotion} className="autopilot-workspace-backdrop fixed inset-0 z-40 bg-background/70" />
          <Dialog.Content
            className="autopilot-workspace fixed inset-0 z-40 flex h-[100dvh] w-full flex-col bg-background focus:outline-none"
            data-motion={entryMotion}
            style={animationStyle}
            onEscapeKeyDown={(event) => { if (!dismissible) event.preventDefault(); }}
            onInteractOutside={(event) => { if (!dismissible) event.preventDefault(); }}
            onAnimationEnd={(event) => {
              if (event.target === event.currentTarget && !open) finishClose();
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              finishClose();
              setCloseAutoFocusPending(true);
            }}
          >
            <header className="flex shrink-0 items-center gap-4 border-b border-border px-4 py-3 sm:px-8">
              <Button
                variant="ghost"
                disabled={!dismissible}
                onClick={() => setOpen(false)}
                className="min-h-11 gap-2"
              >
                <ArrowLeft className="h-4 w-4" /> {backLabel}
              </Button>
              <Dialog.Title className="text-base font-semibold">
                {title}
              </Dialog.Title>
              <Dialog.Description className="sr-only">
                {description}
              </Dialog.Description>
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              <div className="mx-auto max-w-6xl px-4 py-6 sm:px-8 sm:py-8">
                {children}
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
